import type { Dayjs } from '@atlas/tool/box/date/dayjs';
import {
    allowAnyValue,
    contains,
    doesNotContain,
    equal,
    exactly,
    greater,
    greaterOrEqual,
    is,
    isNot,
    isNotNull,
    isNotOneOf,
    isOneOf,
    less,
    lessOrEqual,
    notEqual,
    validOperators,
} from '@atlas/tool/box/conditional-fields/operators';
import isNotNullable from '@atlas/tool/box/isNotNullable';

function operatorIsOneOfOrNot(ruleValue: any, userValue: any, isOneOf = true): boolean {
    let result = false;

    if (Array.isArray(userValue) && Array.isArray(ruleValue)) {
        result = ruleValue.some((value) => userValue.includes(value));
    } else if (Array.isArray(ruleValue) && !Array.isArray(userValue)) {
        result = ruleValue.includes(userValue);
    }

    if (!isOneOf) {
        return !result;
    }

    return result;
}

function isScalarOperator(operator: string, ruleValue: TValue, userValue: TValue): boolean {
    if (Array.isArray(ruleValue) || Array.isArray(userValue)) {
        return false;
    }

    try {
        switch (operator) {
            case equal:
            case exactly:
                return ruleValue === userValue;
            case notEqual:
                return ruleValue !== userValue;
            case greaterOrEqual:
                return Boolean(ruleValue && userValue && ruleValue >= userValue);
            case greater:
                return Boolean(ruleValue && userValue && ruleValue > userValue);
            case lessOrEqual:
                return Boolean(ruleValue && userValue && ruleValue <= userValue);
            case less:
                return Boolean(ruleValue && userValue && ruleValue < userValue);
            default:
                return false;
        }
    } catch (error) {
        return false;
    }
}

function operatorContains(ruleValue: TValue, userValue: TValue) {
    return Boolean(userValue && ruleValue && (userValue as string).includes(ruleValue as string));
}

export default function valueComparator(operator: string, ruleValue: TValue, userValue: TValue): boolean {
    if (!validOperators.includes(operator)) {
        return false;
    }

    switch (operator) {
        case is:
            return ruleValue === userValue;
        case isNot:
            return ruleValue !== userValue;
        case isNotNull:
            return isNotNullable(userValue);
        case contains:
            return operatorContains(ruleValue, userValue);
        case doesNotContain:
            return !operatorContains(ruleValue, userValue);
        case isOneOf:
            return operatorIsOneOfOrNot(ruleValue, userValue);
        case isNotOneOf:
            return operatorIsOneOfOrNot(ruleValue, userValue, false);
        case allowAnyValue:
            return true;
        default:
            return isScalarOperator(operator, ruleValue, userValue);
    }
}

export type TValue = string | number | boolean | Date | Array<string | number | Date> | Dayjs | unknown | undefined;

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('comparator', async () => {
        it('compares values correctly', () => {
            expect(valueComparator(is, 'Hello', 'Hello')).toBeTruthy();
        });
    });
}

// #endregion
